















































import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
import { getBranchesOfClient } from "@/data/repository/ClientRepository";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { getTemplateIdFromSerialNumber, isClientPanel, isDeviceOverview } from "@/data/models/DeviceSettingsManager";
import { isDeviceAvailable } from "@/utils/DeviceManagementUtils";
import { formatDateShort, timestampToDate } from "@/utils/DateFormatter";
import { UserModule } from "@/store/modules/UserModule";
import { setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { getIresDeviceCategoryFromSerialNumber, IresDeviceCategory } from "@/data/models/IresDevice";
import { getGenericDeviceDetails, getTemplateById } from "@/data/repository/GenericDeviceRepository";
import { GenericDeviceProjectAssignment } from "@/data/models/GenericDeviceAssignment";
import { getDeviceAssignmentsByDeviceId } from "@/data/repository/GenericDeviceAssignmentRepository";
import DeviceDetailsDialog from "../allDevices/DeviceDetailsDialog.vue";
import ClientPanel, { ClientPanelExtended } from "@/data/models/ClientPanel";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { joinToString } from "@/utils/LocalisationHelper";
import Vehicle from "@/data/models/Vehicle";

@Component({
  components: {
    "device-details-dialog": DeviceDetailsDialog,
  }
})
export default class VehicleDeviceItemComponent extends Vue {
  @Prop() device!: DeviceOverview | ClientPanelExtended;
  @Prop() elevation!: number;
  @Prop({ default: false }) isSelectionMode!: boolean;
  @Prop({ default: false }) isSelected!: boolean;
  @Prop({ default: false }) isLoadingDevices!: boolean;
  @Prop({ default: false }) disableAll!: boolean;
  @Prop({ default: false }) isReadOnly!: boolean;
  @Prop({ default: null }) branch!: string | null | undefined;
  @Prop({ default: false }) isAllowedToDefineOwnDeviceCategoriesAndTemplates!: boolean;
  @Prop() vehicle!: Vehicle;
  
  @Prop({ default: "" })
  backgroundStyle!: string;

  @Prop({ default: false })
  forceUpdateBackgroundColor!: boolean;

  isDeviceSelected = false;

  @Ref("deviceDetailsDialog")
  deviceDetailsDialog!: DeviceDetailsDialog;

  @Watch("isSelected")
  onIsSelectedChanged() {
    this.isDeviceSelected = this.isSelected;
  }

  get clientPanel() {
    return this.isClientPanel
      ? this.device as ClientPanel
      : null;
  }

  get deviceOverview() {
    return isDeviceOverview(this.device)
      ? this.device as DeviceOverview
      : null;
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  get deviceTemplate() {
    const templateId = this.deviceOverview
      ? this.deviceOverview.templateId
      : getTemplateIdFromSerialNumber(this.device.serialNumber);

    return getTemplateById(
      this.deviceSettingsJson,
      templateId
    );
  }

  get deviceId() {
    const device = this.device;
    const inventoryNumber = device.inventoryNumber;

    return isClientPanel(device)
      ? inventoryNumber && inventoryNumber !== ""
        ? inventoryNumber
        : device.serialNumber
      : inventoryNumber;
  }

  get branchName() {
    const branchNumber = this.device.branch;

    if (!branchNumber) {
      return "";
    }

    const branches = getBranchesOfClient();
    const deviceBranch = branches.find(branch => branchNumber.toString() === branch.branchNumber.toString());

    const branchName = deviceBranch?.name;

    if (!branchName) {
      return "";
    }

    if (this.isLoadingDevices) {
      return branchName;
    }

    return joinToString([this.$t("common.fromAlt").toString(), branchName], " ");
  }

  get lastMovedAtInfo() {
    const deviceLastMovedAt = this.device.deviceLastMoved?.recordedAt;
    if (!deviceLastMovedAt) {
      return "";
    }

    const movedAt = formatDateShort(deviceLastMovedAt).toString();
    const movedBy = UserModule.userNicknameFromRecordInfo(
      this.device.deviceLastMoved
    );

    if (!movedBy) {
      return movedAt;
    }

    return this.$t("vehicles.devices.deviceMoveInfo", [movedAt, movedBy]);
  }

  get isDisabled() {
    return this.disableAll || this.isAssignedToProject || !this.isAvailable;
  }

  get isClientPanel() {
    return isClientPanel(this.device);
  }

  get isAssignedToProject() {
    return this.isClientPanel
      ? (this.device as ClientPanelExtended).currentProjectId !== null
      : (this.device as DeviceOverview).currentlyAssigned;
  }

  get isAvailable() {
    return isDeviceAvailable(this.device);  
  }

  get deviceProjectInfo() {
    let projectName = "";

    if (this.isClientPanel) {
      const clientPanel = this.device as ClientPanelExtended;

      projectName = clientPanel.currentProjectName 
        ? this.$t("common.currentProject", [clientPanel.currentProjectName]).toString()
        : clientPanel.lastProjectName 
          ? this.$t("common.lastProject", [clientPanel.lastProjectName]).toString()
          : "";
    }
    else {
      const deviceOverview = this.device as DeviceOverview;

      projectName = deviceOverview.currentlyAssigned 
        ? this.$t("common.currentProject", [deviceOverview.projectName]).toString()
        : deviceOverview.projectName
          ? this.$t("common.lastProject", [deviceOverview.projectName]).toString()
          : "";
    }

    return projectName;
  }

  get deviceProjectTimeInfo() {
    const projectName = this.deviceProjectInfo;
    let projectTimeInfo = "";

    if (this.isClientPanel) {
      const clientPanel = this.device as ClientPanelExtended;

      if (projectName !== "") {
        const startDate = clientPanel.lastProjectAssignmentTimeRange?.startDate;
        const endDate = clientPanel.lastProjectAssignmentTimeRange?.endDate;

        const startYear = startDate ? timestampToDate(startDate)?.getFullYear() : null;
        const endYear = endDate ? timestampToDate(endDate)?.getFullYear() : null;

        if (clientPanel.currentProjectAssignmentTimeRange) {
          projectTimeInfo = this.$t("common.since", [formatDateShort(
            clientPanel.currentProjectAssignmentTimeRange?.startDate,
            false,
            true,
            undefined
          )]).toString();
        }
        else if (endDate && startDate) {
          const formattedStartDate = formatDateShort(startDate, false, true, startYear !== endYear);
          const formattedEndDate = formatDateShort(endDate, false, true, startYear !== endYear);

          if (formattedStartDate !== formattedEndDate) {
            projectTimeInfo = this.$t("allDevices.formatLastProjectTimeInfo", [
              formattedStartDate,
              formattedEndDate
            ]).toString()
          }
          else {
            projectTimeInfo = formattedStartDate;
          }
        }
      }
    }
    else {
      const deviceOverview = this.device as DeviceOverview;

      if (projectName !== "") {
        const startDate = deviceOverview.projectAssignmentTimeRange?.startDate;
        const endDate = deviceOverview.projectAssignmentTimeRange?.endDate;

        const startYear = startDate ? timestampToDate(startDate)?.getFullYear() : null;
        const endYear = endDate ? timestampToDate(endDate)?.getFullYear() : null;

        if (deviceOverview.currentlyAssigned && startDate) {
          projectTimeInfo = this.$t("common.since", [formatDateShort(
            startDate, 
            false, 
            true, 
            undefined
            )]).toString();
        }
        else if (endDate && startDate) {
          const formattedStartDate = formatDateShort(startDate, false, true, startYear !== endYear);
          const formattedEndDate = formatDateShort(endDate, false, true, startYear !== endYear);

          if (formattedStartDate !== formattedEndDate) {
            projectTimeInfo = this.$t("allDevices.formatLastProjectTimeInfo", [
              formattedStartDate,
              formattedEndDate
            ]).toString()
          }
          else {
            projectTimeInfo = formattedStartDate;
          }
        }
      }
    }

    return projectTimeInfo;
  }

  mounted() {
    this.isDeviceSelected = this.isSelected;
  }

  async onSelectDevice() {
    if (this.isReadOnly) {
      return;
    }

    if (this.isSelectionMode && !this.isAssignedToProject) {
      this.$emit("selectDevice");
    }
    else if (!this.isSelectionMode) {
      this.$emit("selectDevice");

      const deviceId = this.device.id ?? "";

      setLastVisitedItem(
        deviceId,
        StorageKeyLastVisitedItemLevel.LEVEL_3, 
      );

      const titlePrefix = this.isClientPanel
        ? getIresDeviceCategoryFromSerialNumber(this.device.serialNumber) === IresDeviceCategory.PANEL
          ? this.$t("common.panel").toString()
          : this.$t("common.box").toString()
        : this.$t("common.device").toString();

      const titleSuffix = this.device.inventoryNumber;

      const title = titlePrefix + " " + titleSuffix;

      const deviceDetails = this.isClientPanel
        ? null
        : await getGenericDeviceDetails(deviceId);
      
      const deviceAssignments: GenericDeviceProjectAssignment[] = this.isClientPanel
        ? []
        : await getDeviceAssignmentsByDeviceId(deviceId);

      this.deviceDetailsDialog.show(title, deviceDetails, deviceAssignments, this.vehicle, undefined, true);
    }
  }
}
